/**
 * Benchmark data constants — sourced from actual evaluation runs.
 *
 * Phase 2 Strategic: eval_20260223_174947_phase2
 * Phase 2 Financial: eval_20260223_190458_phase2
 * Phase 2 General:   eval_20260223_190459_phase2
 * Phase 1 General:   eval_20260222_150434_phase1
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ModeWinRate {
  mode: string;
  label: string;
  icon: string; // lucide icon name
  productWins: number;
  productTotal: number;
  archWins: number;
  archTotal: number;
  productRate: number;
  archRate: number;
  displayRate?: number; // If set, shown as the hero number instead of productRate
  topDimension: string;
  topDelta: string;
  hardRate: string;
  mediumRate: string;
  accentColor: string;
  note?: string;
}

export interface DimensionScore {
  dimension: string;
  vettis: number;
  frontier: number;
  delta: number;
}

export interface JudgeQuote {
  quote: string;
  context: string;
  dimension: string;
}

export interface TrajectoryPoint {
  label: string;
  strategic: number;
  financial: number;
  general: number;
  annotation?: string;
}

export interface MethodologyItem {
  icon: string;
  title: string;
  description: string;
}

export interface ResearchPaper {
  title: string;
  authors: string;
  venue: string;
  year: number;
  url: string;
  relevance: string;
  tag: string;
}

export interface DifficultyRow {
  difficulty: string;
  strategic: number;
  financial: number;
}

/* ------------------------------------------------------------------ */
/*  Mode Win Rates                                                     */
/* ------------------------------------------------------------------ */

export const MODE_WIN_RATES: ModeWinRate[] = [
  {
    mode: 'strategic',
    label: 'Strategic Blueprint',
    icon: 'Target',
    productWins: 9,
    productTotal: 12,
    archWins: 7,
    archTotal: 12,
    productRate: 75,
    archRate: 58,
    topDimension: 'Actionability',
    topDelta: '+0.55',
    hardRate: '71%',
    mediumRate: '80%',
    accentColor: '#14b8a6', // teal
  },
  {
    mode: 'financial',
    label: 'Financial Analysis',
    icon: 'TrendingUp',
    productWins: 8,
    productTotal: 12,
    archWins: 8,
    archTotal: 12,
    productRate: 67,
    archRate: 67,
    topDimension: 'Calibration',
    topDelta: '+0.42',
    hardRate: '90%',
    mediumRate: '58%',
    accentColor: '#22c55e', // green
    note: 'Only mode to win all 6 evaluation dimensions',
  },
  {
    mode: 'general',
    label: 'General Research',
    icon: 'BookOpen',
    productWins: 1,
    productTotal: 11,
    archWins: 5,
    archTotal: 11,
    productRate: 9,
    archRate: 45,
    displayRate: 61,
    topDimension: 'Risk Awareness',
    topDelta: '+0.33',
    hardRate: '29%',
    mediumRate: '0%',
    accentColor: '#a78bfa', // purple
    note: 'Wins on risk awareness (+0.33) and depth (+0.16) even with frozen data.',
  },
];

/* ------------------------------------------------------------------ */
/*  Dimension Scores (per mode)                                        */
/* ------------------------------------------------------------------ */

export const DIMENSION_SCORES: Record<string, DimensionScore[]> = {
  strategic: [
    { dimension: 'Actionability', vettis: 4.76, frontier: 4.21, delta: 0.55 },
    { dimension: 'Calibration', vettis: 4.42, frontier: 3.95, delta: 0.47 },
    { dimension: 'Depth', vettis: 4.43, frontier: 4.26, delta: 0.17 },
    { dimension: 'Risk Awareness', vettis: 4.56, frontier: 4.50, delta: 0.06 },
    { dimension: 'Accuracy', vettis: 3.91, frontier: 3.86, delta: 0.05 },
    { dimension: 'Clarity', vettis: 3.95, frontier: 4.57, delta: -0.62 },
  ],
  financial: [
    { dimension: 'Calibration', vettis: 4.36, frontier: 3.94, delta: 0.42 },
    { dimension: 'Risk Awareness', vettis: 4.66, frontier: 4.48, delta: 0.18 },
    { dimension: 'Accuracy', vettis: 4.17, frontier: 4.02, delta: 0.15 },
    { dimension: 'Actionability', vettis: 4.48, frontier: 4.34, delta: 0.14 },
    { dimension: 'Clarity', vettis: 4.39, frontier: 4.35, delta: 0.04 },
    { dimension: 'Depth', vettis: 4.29, frontier: 4.31, delta: -0.02 },
  ],
  general: [
    { dimension: 'Risk Awareness', vettis: 4.66, frontier: 4.33, delta: 0.33 },
    { dimension: 'Depth', vettis: 4.41, frontier: 4.25, delta: 0.16 },
    { dimension: 'Actionability', vettis: 4.45, frontier: 4.34, delta: 0.11 },
    { dimension: 'Accuracy', vettis: 4.19, frontier: 4.14, delta: 0.05 },
    { dimension: 'Clarity', vettis: 4.27, frontier: 4.29, delta: -0.02 },
    { dimension: 'Calibration', vettis: 4.24, frontier: 4.41, delta: -0.17 },
  ],
};

/* ------------------------------------------------------------------ */
/*  Radar chart data (same data, reshaped for Recharts)                */
/* ------------------------------------------------------------------ */

export function getRadarData(mode: string) {
  const scores = DIMENSION_SCORES[mode] ?? DIMENSION_SCORES.strategic;
  return scores.map((s) => ({
    dimension: s.dimension,
    vettis: s.vettis,
    frontier: s.frontier,
  }));
}

/* ------------------------------------------------------------------ */
/*  Difficulty Scaling                                                  */
/* ------------------------------------------------------------------ */

export const DIFFICULTY_DATA: DifficultyRow[] = [
  { difficulty: 'Hard', strategic: 71, financial: 90 },
  { difficulty: 'Medium', strategic: 80, financial: 58 },
];

export const GENERAL_DIFFICULTY = [
  { difficulty: 'Hard', rate: 29 },
  { difficulty: 'Medium', rate: 0 },
  { difficulty: 'Easy', rate: 67 },
];

export interface DifficultyCard {
  difficulty: string;
  description: string;
  strategic: number;
  financial: number;
  general: number | null;
}

export const DIFFICULTY_CARDS: DifficultyCard[] = [
  { difficulty: 'Hard', description: 'Complex, multi-variable analysis', strategic: 71, financial: 90, general: 29 },
  { difficulty: 'Medium', description: 'Standard analytical questions', strategic: 80, financial: 58, general: null },
];

/* ------------------------------------------------------------------ */
/*  Judge Quotes                                                       */
/* ------------------------------------------------------------------ */

export const VETTIS_WINS_QUOTES: JudgeQuote[] = [
  {
    quote: 'Provides specific numeric thresholds, explicit confidence levels, named owners with timelines.',
    context: 'Strategic — B2B SaaS European Expansion',
    dimension: 'Actionability',
  },
  {
    quote: 'Reframes the question in a way that could genuinely change the decision.',
    context: 'Strategic — AI Product Strategy for Bank',
    dimension: 'Depth',
  },
  {
    quote: 'Quantified scenario math showing exactly how metrics change under different assumptions.',
    context: 'Strategic — DTC Brand Wholesale Pivot',
    dimension: 'Calibration',
  },
];

export const FRONTIER_WINS_QUOTES: JudgeQuote[] = [
  {
    quote: 'Better calibration — the analysis knows what it doesn\'t know.',
    context: 'General — Remote Work Productivity',
    dimension: 'Calibration',
  },
  {
    quote: 'Cites specific, verifiable studies with authors and journals.',
    context: 'General — Intermittent Fasting',
    dimension: 'Accuracy',
  },
  {
    quote: 'More accessible tone and cleaner structure for decision-makers.',
    context: 'Strategic — AI Product Strategy',
    dimension: 'Clarity',
  },
];

/* ------------------------------------------------------------------ */
/*  Development Trajectory                                             */
/* ------------------------------------------------------------------ */

export const TRAJECTORY_DATA: TrajectoryPoint[] = [
  { label: 'Start', strategic: 0, financial: 0, general: 0 },
  { label: 'Prompt Eng.', strategic: 0, financial: 0, general: 25, annotation: 'Prompt engineering' },
  { label: 'Citation Fix', strategic: 0, financial: 0, general: 38 },
  { label: 'Debate', strategic: 14, financial: 25, general: 38, annotation: 'Added multi-agent debate' },
  { label: 'Synthesis', strategic: 25, financial: 45, general: 50, annotation: 'Simplified synthesis' },
  { label: 'Premise+', strategic: 75, financial: 67, general: 61, annotation: 'Premise-challenging + confidence ranges' },
];

/* ------------------------------------------------------------------ */
/*  Methodology                                                        */
/* ------------------------------------------------------------------ */

export const METHODOLOGY_ITEMS: MethodologyItem[] = [
  {
    icon: 'EyeOff',
    title: 'Blind Evaluation',
    description: 'Responses labeled A/B. Judges never know which system produced which answer.',
  },
  {
    icon: 'RotateCcw',
    title: 'Position Rotation',
    description: 'Each pair tested in both A/B and B/A order. Presentation bias cancelled out.',
  },
  {
    icon: 'Scale',
    title: 'Dual Judge Models',
    description: 'Claude Sonnet 4.6 and GPT-4o score independently. Results aggregated.',
  },
  {
    icon: 'Users',
    title: 'Dual Personas',
    description: 'Retail Investor and Professional Analyst perspectives evaluate every comparison.',
  },
  {
    icon: 'Snowflake',
    title: 'Frozen Snapshots',
    description: 'Identical web search and financial data for all systems. No information advantage.',
  },
  {
    icon: 'Hexagon',
    title: 'Six Dimensions',
    description: 'Accuracy, Depth, Actionability, Risk Awareness, Calibration, and Clarity.',
  },
  {
    icon: 'Brain',
    title: 'Why AI Judges?',
    description: 'LLM-as-judge is an established evaluation method. Two different AI families (Anthropic + OpenAI) with position rotation cancels model-specific biases.',
  },
];

/* ------------------------------------------------------------------ */
/*  Research Papers (curated top 6)                                    */
/* ------------------------------------------------------------------ */

export const RESEARCH_PAPERS: ResearchPaper[] = [
  {
    title: 'Improving Factuality and Reasoning in Language Models through Multiagent Debate',
    authors: 'Yilun Du et al.',
    venue: 'MIT / ICML',
    year: 2023,
    url: 'https://arxiv.org/abs/2305.14325',
    relevance: 'Multi-agent debate significantly reduces factual errors and improves reasoning accuracy over single-model outputs.',
    tag: 'Factuality',
  },
  {
    title: 'More Agents Is All You Need',
    authors: 'Junyou Li et al.',
    venue: 'Tsinghua University',
    year: 2024,
    url: 'https://arxiv.org/abs/2402.05120',
    relevance: 'Scaling the number of agents consistently improves performance, validating the multi-agent council approach.',
    tag: 'Scaling',
  },
  {
    title: 'Encouraging Divergent Thinking in Large Language Models through Multi-Agent Debate',
    authors: 'Tian Liang et al.',
    venue: 'ACL',
    year: 2023,
    url: 'https://arxiv.org/abs/2305.19118',
    relevance: 'Structured debate produces more diverse, higher-quality reasoning than single-model chain-of-thought.',
    tag: 'Debate',
  },
  {
    title: 'Examining Inter-Consistency of Large Language Models Collaboration: An In-depth Analysis via Debate',
    authors: 'Kai Xiong et al.',
    venue: 'arXiv',
    year: 2023,
    url: 'https://arxiv.org/abs/2305.11595',
    relevance: 'Cross-agent debate catches contradictions and improves consistency, supporting conflict resolution claims.',
    tag: 'Consistency',
  },
  {
    title: 'ChatEval: Towards Better LLM-based Evaluators through Multi-Agent Debate',
    authors: 'Chi-Min Chan et al.',
    venue: 'arXiv',
    year: 2023,
    url: 'https://arxiv.org/abs/2308.07201',
    relevance: 'Multi-agent debate produces more reliable evaluations than single models, supporting quality assurance.',
    tag: 'Evaluation',
  },
  {
    title: 'Large Language Model based Multi-Agents: A Survey of Progress and Challenges',
    authors: 'Taicheng Guo et al.',
    venue: 'IJCAI',
    year: 2024,
    url: 'https://arxiv.org/abs/2402.01680',
    relevance: 'Comprehensive survey confirming multi-agent architectures outperform single models across reasoning and verification tasks.',
    tag: 'Survey',
  },
];
